import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { GameConfig } from '../config/GameConfig';
import { BaseWeapon, BaseProjectile } from './BaseWeapon';

export class Projectile extends BaseProjectile {
  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, '');

    this.damage = GameConfig.WEAPON_DATA.BASE_DAMAGE;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    // Create claw projectile texture
    this.createClawTexture(scene);
    this.setTexture('projectile-claw');
  }

  private createClawTexture(scene: Phaser.Scene): void {
    const graphics = scene.add.graphics();
    const size = 16;
    const halfSize = size / 2;

    // Background glow
    graphics.fillStyle(0xFFFFFF, 0.2);
    graphics.fillCircle(halfSize, halfSize, 7);

    // Three sharp claws with gradient effect
    // Left claw
    graphics.fillStyle(0xF0F0F0, 1);
    graphics.fillTriangle(
      halfSize - 3, halfSize - 5,
      halfSize - 4, halfSize + 5,
      halfSize - 2, halfSize + 5
    );
    graphics.fillStyle(0xFFFFFF, 1);
    graphics.fillTriangle(
      halfSize - 3, halfSize - 5,
      halfSize - 3.5, halfSize + 5,
      halfSize - 2.5, halfSize + 5
    );

    // Middle claw (tallest)
    graphics.fillStyle(0xF0F0F0, 1);
    graphics.fillTriangle(
      halfSize, halfSize - 6,
      halfSize - 1, halfSize + 6,
      halfSize + 1, halfSize + 6
    );
    graphics.fillStyle(0xFFFFFF, 1);
    graphics.fillTriangle(
      halfSize, halfSize - 6,
      halfSize - 0.5, halfSize + 6,
      halfSize + 0.5, halfSize + 6
    );

    // Right claw
    graphics.fillStyle(0xF0F0F0, 1);
    graphics.fillTriangle(
      halfSize + 3, halfSize - 5,
      halfSize + 2, halfSize + 5,
      halfSize + 4, halfSize + 5
    );
    graphics.fillStyle(0xFFFFFF, 1);
    graphics.fillTriangle(
      halfSize + 3, halfSize - 5,
      halfSize + 2.5, halfSize + 5,
      halfSize + 3.5, halfSize + 5
    );

    // Add sharp tips
    graphics.fillStyle(0xDDDDDD, 1);
    graphics.fillCircle(halfSize - 3, halfSize - 5, 1);
    graphics.fillCircle(halfSize, halfSize - 6, 1);
    graphics.fillCircle(halfSize + 3, halfSize - 5, 1);

    graphics.generateTexture('projectile-claw', size, size);
    graphics.destroy();
  }
}

export class WeaponSystem extends BaseWeapon {
  private attackSpeedMultiplier: number = 1;
  private projectileSpeedMultiplier: number = 1;
  private projectileCount: number = 1;

  constructor(scene: Phaser.Scene, player: Player, enemies: Phaser.Physics.Arcade.Group) {
    super(scene, player, enemies, Projectile);
    this.startAutoAttack();
  }

  protected startAutoAttack(): void {
    this.attackTimer = this.scene.time.addEvent({
      delay: GameConfig.WEAPON_DATA.BASE_COOLDOWN,
      callback: this.attack,
      callbackScope: this,
      loop: true
    });
  }

  protected attack(): void {
    // Find nearest enemy
    const nearestEnemy = this.findNearestEnemy();

    if (nearestEnemy) {
      // Fire multiple projectiles with slight angle spread
      const baseAngle = Math.atan2(nearestEnemy.y - this.player.y, nearestEnemy.x - this.player.x);
      const spreadAngle = 0.2; // radians

      for (let i = 0; i < this.projectileCount; i++) {
        let angle = baseAngle;
        if (this.projectileCount > 1) {
          // Spread projectiles in an arc
          const offset = (i - (this.projectileCount - 1) / 2) * spreadAngle;
          angle = baseAngle + offset;
        }
        this.fireProjectileAtAngle(angle);
      }
    }
  }

  private findNearestEnemy(): Enemy | null {
    const enemyArray = this.enemies.getChildren() as Enemy[];

    if (enemyArray.length === 0) return null;

    let nearest: Enemy | null = null;
    let minDistance = Infinity;

    for (const enemy of enemyArray) {
      if (!enemy.active) continue;

      const distance = Phaser.Math.Distance.Between(
        this.player.x,
        this.player.y,
        enemy.x,
        enemy.y
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearest = enemy;
      }
    }

    return nearest;
  }

  private fireProjectileAtAngle(angle: number): void {
    const projectile = new Projectile(this.scene, this.player.x, this.player.y);
    this.projectiles.add(projectile);

    // Apply damage multiplier
    projectile.damage = GameConfig.WEAPON_DATA.BASE_DAMAGE * this.damageMultiplier;

    // Calculate direction from angle
    const direction = new Phaser.Math.Vector2(
      Math.cos(angle),
      Math.sin(angle)
    );

    direction.normalize();
    direction.scale(GameConfig.WEAPON_DATA.PROJECTILE_SPEED * this.projectileSpeedMultiplier);

    projectile.setVelocity(direction.x, direction.y);

    // Add spinning animation
    projectile.setAngularVelocity(720); // Spin fast

    // Auto-destroy after 3 seconds
    this.scene.time.delayedCall(3000, () => {
      if (projectile.active) {
        projectile.destroy();
      }
    });
  }

  public updateAttackSpeed(multiplier: number): void {
    this.attackSpeedMultiplier = multiplier;

    // Restart attack timer with new speed
    if (this.attackTimer) {
      this.attackTimer.destroy();
    }

    const newDelay = GameConfig.WEAPON_DATA.BASE_COOLDOWN / this.attackSpeedMultiplier;
    this.attackTimer = this.scene.time.addEvent({
      delay: newDelay,
      callback: this.attack,
      callbackScope: this,
      loop: true
    });
  }

  public updateProjectileSpeed(multiplier: number): void {
    this.projectileSpeedMultiplier *= multiplier;
  }

  public addProjectile(): void {
    this.projectileCount++;
  }
}
